import { PrinterIcon } from "@heroicons/react/24/outline";
import { DateTime } from "luxon";

import {
  DropOffSlot,
  IDropOffDragItem,
  IDropOffLeadRow,
} from "../../models/DropOffPlanner";
import classNames from "../../utils/classNames";
import DropOffSlotZone from "./DropOffSlotZone";

interface DropOffDayColumnProps {
  date: string;
  leads: IDropOffLeadRow[];
  onDrop: (item: IDropOffDragItem, date: string, slot: DropOffSlot) => void;
  onPrint: (date: string) => void;
}

const slotFor = (lead: IDropOffLeadRow): DropOffSlot =>
  lead.scheduledCollectionSlot === DropOffSlot.PM ? DropOffSlot.PM : DropOffSlot.AM;

export default function DropOffDayColumn({
  date,
  leads,
  onDrop,
  onPrint,
}: DropOffDayColumnProps) {
  const dt = DateTime.fromISO(date);
  const isToday = dt.hasSame(DateTime.local(), "day");

  const amLeads = leads.filter((l) => slotFor(l) === DropOffSlot.AM);
  const pmLeads = leads.filter((l) => slotFor(l) === DropOffSlot.PM);

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-md flex flex-col min-w-[240px] overflow-y-auto">
      <div className="px-3 py-2 border-b border-gray-200 bg-white rounded-t-md flex items-center justify-between">
        <span
          className={classNames(
            "text-sm font-semibold",
            isToday ? "text-primary-700" : "text-gray-800"
          )}
        >
          {dt.toFormat("ccc")} {dt.toFormat("d LLL")}
        </span>
        <div className="flex items-center gap-x-1.5">
          <span className="text-xs text-gray-500">{leads.length}</span>
          <button
            type="button"
            onClick={() => onPrint(date)}
            className="inline-flex h-6 w-6 items-center justify-center rounded border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700"
            title="Print drop off summary for this day"
          >
            <PrinterIcon className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      <div className="flex-1 flex flex-col">
        <DropOffSlotZone
          date={date}
          slot={DropOffSlot.AM}
          label="AM"
          leads={amLeads}
          onDrop={onDrop}
        />
        <DropOffSlotZone
          date={date}
          slot={DropOffSlot.PM}
          label="PM"
          leads={pmLeads}
          onDrop={onDrop}
        />
      </div>
    </div>
  );
}
